import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next";
import { FormField, Icon, Button } from '@weirgroup/weir-dls'
import { getPartLookup } from "../../api/part-lookup.api"
import { PointsTable } from "./components/points-table";
import { PartLookupComponentType, PartLookupType } from "../../types/part-lookup.type";
import { AttachmentInfoTable } from "./components/attachment-info-table";
import { LoadingSpinner } from "../../components/loading-spinner";
import "./styles.scss";
import { useDarkMode } from "../../hooks/use-darkmode";
import { useAnalyticsEvent } from "../../hooks/useAnalyticsEvent";

const MAX_RETRY_COUNT = 3;
const AUTO_RETRY_DELAY = 3000;

export const PartLookUp = () => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const [serialNumber, setSerialNumber] = useState<string>("")
    const [attachmentInfo, setAttachmentInfo] = useState<PartLookupType | null>(null)
    const [points, setPoints] = useState<PartLookupComponentType[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [noResults, setNoResults] = useState<boolean>(false)
    const [errorResults, setErrorResults] = useState<boolean>(false)
    const [retryCount, setRetryCount] = useState<number>(0)
    const [autoRetryExhausted, setAutoRetryExhausted] = useState<boolean>(false)
    const { t } = useTranslation();
    const {darkMode} = useDarkMode();
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clean up any pending timeouts when component unmounts
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);
    useEffect(() => {
        logAnalyticsEvent('user_viewed_part_lookup_page', { description: 'User viewed part lookup page' });
    },[]);
    // Auto-retry effect that triggers when errorResults becomes true
    useEffect(() => {
        if (errorResults && retryCount < MAX_RETRY_COUNT && !autoRetryExhausted) {
            retryTimeoutRef.current = setTimeout(() => {
                // Auto-retry
                setRetryCount(prevCount => prevCount + 1);
                setErrorResults(false);
                handleSearchSerialNumber();
                // Check if we've reached the maximum number of retries
                if (retryCount + 1 >= MAX_RETRY_COUNT) {
                    setAutoRetryExhausted(true);
                }
            }, AUTO_RETRY_DELAY);
        }
        
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, [errorResults, retryCount, autoRetryExhausted]);

    const handleSearchSerialNumber = () =>{
        if (serialNumber) {
            setIsLoading(true)
            setAttachmentInfo(null);
            setPoints([]);

            getPartLookup(serialNumber).then((result: any) => {
                setIsLoading(false)
                setAttachmentInfo(result.data.data);
                setPoints(result.data.data.components)
                if (!result.data.data.serial_number) {
                    setNoResults(true);
                    setErrorResults(false);
                    setRetryCount(0);
                }
                logAnalyticsEvent('user_search_a_serial_number', { description: 'User searched a serial number' });
            }).catch((err) => {
                setIsLoading(false);
                setNoResults(false);
                setErrorResults(true);
            })
        }
    }

    const handleManualRetry = () => {
        // Reset the retry counter and auto-retry exhausted flag to start the automatic retry process again
        setRetryCount(0);
        setAutoRetryExhausted(false);
        setNoResults(false);
        setErrorResults(false);
        handleSearchSerialNumber();
    }

    const renderSearchResults = () => {
        if (attachmentInfo?.serial_number) {
            return (
                <div id="attachment-data">
                    <AttachmentInfoTable attachmentInfo={attachmentInfo} darkMode={darkMode} />
                    <PointsTable points={points} darkMode={darkMode} />
                </div>
            );
        } else if (noResults) {
            return (
                <div className="no-results">
                    <p>
                        <Icon name="Info" darkMode={darkMode}/>
                        {t('noResultPartLookup').toString()} "<span>{serialNumber}</span>". {t('noResultSecondPartLookup').toString()}.
                    </p>
                </div>
            );
        } else if (errorResults) {
            return (
                <div className="no-results">
                    <p>
                        <Icon name="Info" darkMode={darkMode}/>
                        {t('partLookupTimeoutMessage').toString()}
                    </p>
                    
                    <div className="retry-section">
                        {retryCount > 0 && !autoRetryExhausted && (
                            <p className={`retry-count ${darkMode ? 'dark' : 'light'}`}>
                                {t('retryAttempts', { count: retryCount, max: MAX_RETRY_COUNT })}
                                <span className="auto-retry-indicator">
                                    &nbsp;&nbsp;&nbsp;{t('autoRetrying')}...
                                </span>
                            </p>
                        )}
                        
                        {retryCount > 0 && autoRetryExhausted && (
                            <p className={`retry-count ${darkMode ? 'dark' : 'light'}`}>
                                {t('maxRetriesReached')}
                            </p>
                        )}
                        
                        {autoRetryExhausted && (
                            <Button
                                darkMode={darkMode}
                                label={t("tryAgain")}
                                size="sm"
                                variant="primary"
                                onClick={handleManualRetry}
                            />
                        )}
                    </div>
                </div>
            );
        }
        
        return null;
    }
    
    return (
        <div className="part-lookup-container" style={{backgroundColor:darkMode ? '#3B434C' : '#F6F7F8'}}>
            <div className="header">
                <p className="title" style={{color:darkMode ? 'white' : 'black'}}>{t("partLookupTitle").toString()}</p>
                <div className="search">
                    <FormField
                        darkMode={darkMode}
                        id="serialNum"
                        name="InputBox"
                        size={'md'}
                        placeholder={t("partLookupPlaceholder")}
                        disabled={isLoading}
                        required={true}
                        full
                        value={serialNumber}
                        onChange={(e: any) => {
                            setSerialNumber(e.target.value);
                        }}
                        onKeyPress={(e: any) => {
                            if (e.code == "Enter" || e.code == "NumpadEnter") {
                                setNoResults(false);
                                setErrorResults(false);
                                setRetryCount(0);
                                handleSearchSerialNumber()
                            }
                        }}
                        autoComplete="off"
                        icon={isLoading ? "Horizontal" : "Search"}
                        onIconClick={() => handleSearchSerialNumber()}
                    />
                </div>
            </div>
            {renderSearchResults()}
            {isLoading && (
                <LoadingSpinner darkMode={darkMode} />
            )}
        </div>
    )
}